import { createContext, useEffect, useReducer } from "react";
import {
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from "firebase/auth";
// GLOBAL CUSTOM COMPONENT
import Loading from "app/components/MatxLoading";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebaseInit"; // firebaseInit kullanıyoruz
import LogService from "app/services/LogService.ts";

const initialAuthState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false
};

const reducer = (state, action) => {
  switch (action.type) {
    case "FB_AUTH_STATE_CHANGED": {
      const { isAuthenticated, user } = action.payload;
      return { ...state, isAuthenticated, isInitialized: true, user };
    }

    default: {
      return state;
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: "FIREBASE",
  createUserWithEmail: (email, password) => Promise.resolve(),
  signInWithEmail: (email, password) => Promise.resolve(),
  logout: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const signInWithEmail = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const idTokenResult = await user.getIdTokenResult();
      const role = idTokenResult.claims.role;

      if (role !== "admin" && role !== "superadmin") {
        await auth.signOut();
        const error = new Error("Not authorized");
        error.code = "auth/not-authorized";
        throw error;
      }

      // 🔹 Kullanıcı bilgisine rolü ekleyelim
      dispatch({
        type: "FB_AUTH_STATE_CHANGED",
        payload: {
          isAuthenticated: true,
          user: {
            id: user.uid,
            email: user.email,
            role, // 👈 rolü burada ekliyoruz
            // avatar: user.photoURL,
            name: user.displayName || user.email
          }
        }
      });

      await LogService.addAuditLogClient({
        action: "LOGIN",
        userId: user.uid,
        entityType: "auth",
        description: `${user.email} logged in`
      });

      await LogService.addInfoLog("User logged in", { userId: user.uid, email: user.email });
      return user;

    } catch (error) {
      await LogService.addErrorLog("Login error", error, { email });
      throw error;
    }
  };

  const createUserWithEmail = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    // Firestore'a kayıt
    await setDoc(doc(db, "users", user.uid), {
      email,
      role: "admin", // burada rolü açıkça belirtiyoruz
      createdAt: serverTimestamp(),
    });

    return userCredential;
  };

  const logout = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await signOut(auth);

      // Audit log
      await LogService.addAuditLogClient({
        action: "LOGOUT",
        userId: user.uid,
        entityType: "auth",
        description: `${user.email} logged out`
      });

      // Info log
      await LogService.addInfoLog("User logged out", { userId: user.uid, email: user.email });
    } catch (error) {
      await LogService.addErrorLog("Logout error", error, { userId: user.uid });
      throw error;
    }
  };

  useEffect(() => {
    console.log("Firebase Project:", process.env.REACT_APP_FIREBASE_PROJECT_ID);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult(true); // force refresh
        const role = idTokenResult.claims.role || "user";
        dispatch({
          type: "FB_AUTH_STATE_CHANGED",
          payload: {
            isAuthenticated: true,
            user: {
              id: user.uid,
              email: user.email,
              role: role,
              // avatar: user.photoURL,
              name: user.displayName || user.email
            }
          }
        });
      } else {
        dispatch({
          type: "FB_AUTH_STATE_CHANGED",
          payload: { isAuthenticated: false, user: null }
        });
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  if (!state.isInitialized) return <Loading />;

  return (
    <AuthContext.Provider
      value={{
        ...state,
        logout,
        method: "FIREBASE",
        signInWithEmail,
        createUserWithEmail
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
